<template>
  <div class="guides">
    <div class="w-72 min-w-72 border flex flex-col p-1">
      <el-button
        size="mini"
        type="primary"
        plain
        class="w-full"
        @click="newGuide"
      >
        New Guide
      </el-button>
      <ul class="trigger-marker grow overflow-auto py-1">
        <li
          v-for="item in guides"
          :key="item.id"
          :class="{ active: form && form.id === item.id }"
          @click="openGuide(item)"
        >
          {{ item.title }}
        </li>
      </ul>
    </div>
    <div class="grow border overflow-auto p-2">
      <div v-if="!form" class="text-gray-400 text-center py-8">
        Select a guide or create a new one
      </div>
      <div v-else-if="previewing" class="h-full box">
        <div class="box-title flex items-center justify-between">
          <div class="font-bold">{{ form.title }}</div>
          <el-button size="mini" @click="previewing = false">
            Close Preview
          </el-button>
        </div>
        <iframe class="guide-frame" sandbox="" :srcdoc="previewDocument" />
      </div>
      <el-form v-else size="mini" label-position="top" @submit.native.prevent>
        <el-form-item label="Title" required>
          <el-input v-model="form.title" />
        </el-form-item>
        <el-form-item label="Category" required>
          <el-radio-group v-model="form.category" @change="categoryChanged">
            <el-radio-button label="markdown">Markdown</el-radio-button>
            <el-radio-button label="html">HTML</el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="Content">
          <e-editor
            v-if="form.category === 'html'"
            :key="editorKey"
            :html.sync="form.content"
            :height="800"
          />
          <el-input
            v-else
            v-model="form.content"
            type="textarea"
            :autosize="{ minRows: 30, maxRows: 40 }"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" :loading="saving" @click="save">
            Save
          </el-button>
          <el-button v-if="form.id" type="success" @click="preview">
            Preview
          </el-button>
          <el-button v-if="form.id" type="danger" plain @click="destroy">
            Delete
          </el-button>
        </el-form-item>
      </el-form>
    </div>
  </div>
</template>
<script>
const PREVIEW_STYLE = `
  html, body { margin: 0; padding: 0; background: #fff; }
  body {
    padding: 20px 24px 32px;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
    font-size: 14px;
    line-height: 1.6;
    color: #303133;
  }
  h1, h2, h3, h4 { margin: 0.6em 0 0.4em; font-weight: 600; line-height: 1.3; }
  h1 { font-size: 1.6em; }
  h2 { font-size: 1.35em; }
  h3 { font-size: 1.15em; }
  p, ol, ul, table, pre { margin: 0.5em 0 1em; }
  ol, ul { padding-left: 1.5em; }
  ol { list-style: decimal; }
  ul { list-style: disc; }
  li { margin: 0.25em 0; }
  table { width: 100%; border-collapse: collapse; }
  th, td { border: 1px solid #dcdfe6; padding: 4px 6px; text-align: left; vertical-align: top; }
  code, pre { background: #f5f7fa; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
  pre { padding: 8px; overflow: auto; }
  a { color: #409eff; }
  img { max-width: 100%; }
`;

export default {
  name: "Guides",
  data() {
    return {
      guides: [],
      form: null,
      saving: false,
      previewing: false,
      previewHtml: "",
      renderedHtml: "",
      savedContent: "",
      editorKey: 0,
    };
  },
  computed: {
    previewDocument() {
      return `<!DOCTYPE html><html><head><meta charset="utf-8"><style>${PREVIEW_STYLE}</style></head><body>${
        this.previewHtml || ""
      }</body></html>`;
    },
  },
  created() {
    this.loadGuides();
  },
  beforeDestroy() {
    this.removeEditor();
  },
  methods: {
    async loadGuides() {
      try {
        const response = await this.$http.get("/system/guides.json");
        this.guides = response.data;
      } catch (err) {
        this.$message({
          message: this.errorMessage(err, "Failed to load guides"),
          type: "error",
        });
      }
    },
    newGuide() {
      this.previewing = false;
      this.removeEditor();
      this.savedContent = "";
      this.renderedHtml = "";
      this.form = { title: "", content: "", category: "markdown" };
      this.editorKey += 1;
    },
    openGuide(item) {
      this.previewing = false;
      this.removeEditor();
      this.savedContent = item.content || "";
      this.renderedHtml = item.html || "";
      this.form = this.formFrom(item);
      this.editorKey += 1;
    },
    formFrom(item) {
      return {
        id: item.id,
        title: item.title || "",
        content: item.content || "",
        category: item.category || "markdown",
      };
    },
    categoryChanged() {
      this.previewing = false;
      this.removeEditor();
      this.editorKey += 1;
    },
    preview() {
      this.syncEditor();
      if (
        this.form.category === "markdown" &&
        (this.form.content || "") !== this.savedContent
      ) {
        this.$message({
          message: "Save the guide to preview markdown changes",
          type: "warning",
        });
        return;
      }
      this.previewHtml =
        this.form.category === "html"
          ? this.form.content || ""
          : this.renderedHtml || "";
      this.removeEditor();
      this.previewing = true;
    },
    editors() {
      const editors = window.tinymce && window.tinymce.editors;
      return Array.isArray(editors) ? editors : [];
    },
    syncEditor() {
      if (!this.form || this.form.category !== "html" || !this.$el) return;
      this.editors().forEach((editor) => {
        const element = editor.getElement && editor.getElement();
        if (element && this.$el.contains(element)) {
          this.form.content = editor.getContent();
        }
      });
    },
    removeEditor() {
      if (!this.$el) return;
      this.editors()
        .slice()
        .forEach((editor) => {
          const element = editor.getElement && editor.getElement();
          if (element && this.$el.contains(element)) editor.remove();
        });
    },
    async save() {
      this.syncEditor();
      this.saving = true;
      try {
        const payload = {
          guide: {
            title: this.form.title,
            content: this.form.content,
            category: this.form.category,
          },
        };
        const response = this.form.id
          ? await this.$http.put(`/system/guides/${this.form.id}.json`, payload)
          : await this.$http.post("/system/guides.json", payload);
        this.renderedHtml = response.data.html || "";
        this.form = this.formFrom(response.data);
        this.savedContent = response.data.content || "";
        this.upsert(response.data);
        this.$message({ message: "Saved successfully", type: "success" });
      } catch (err) {
        this.$message({
          message: this.errorMessage(err, "Failed to save guide"),
          type: "error",
        });
      } finally {
        this.saving = false;
      }
    },
    async destroy() {
      try {
        await this.$confirm("Delete this guide?", "Warning", {
          confirmButtonText: "Delete",
          cancelButtonText: "Cancel",
          type: "warning",
        });
      } catch (err) {
        return;
      }
      try {
        await this.$http.delete(`/system/guides/${this.form.id}.json`);
        this.guides = this.guides.filter((item) => item.id !== this.form.id);
        this.form = null;
        this.previewing = false;
        this.$message({ message: "Deleted successfully", type: "success" });
      } catch (err) {
        this.$message({
          message: this.errorMessage(err, "Failed to delete guide"),
          type: "error",
        });
      }
    },
    upsert(guide) {
      const index = this.guides.findIndex((item) => item.id === guide.id);
      if (index >= 0) this.$set(this.guides, index, guide);
      else this.guides.push(guide);
      this.guides.sort((a, b) => a.title.localeCompare(b.title));
    },
    errorMessage(err, fallback) {
      const errors =
        err.response && err.response.data && err.response.data.errors;
      if (Array.isArray(errors)) return errors.join(", ");
      return errors || fallback;
    },
  },
};
</script>
<style lang="scss" scoped>
.guides::v-deep {
  @apply flex gap-1 h-[calc(100vh_-_115px)];
  .e-editor .editor-wrapper {
    width: 100%;
  }
  .guide-frame {
    @apply w-full grow border-0 bg-white;
    min-height: 0;
  }
}
</style>
